import { helpFor } from '@norum/contracts';
import type { WireTask, WireTaskCapability, WireTwinTask, WireTwinTaskRow } from '@/api/wire';
import type { ItemContext, Party, StepAction } from '@/ui/context';
import type { Who } from '@/ui/who';
import { humanise } from '@/ui/words';

/**
 * What a task says about itself (ADR-0155): the board's card, the task's own
 * page, the Today row and the twin's task list all say the same thing,
 * because they are all this one builder over the row the screen already
 * holds. Pure: no hooks, no fetches, and nothing it was not given.
 *
 * What it will not do: show a request's body (a request's words are the
 * asking department's, §18, and the task carries only the summary), guess
 * who made a task when the row does not say, or link to a page the reader
 * may not open. The next step is a rule over the task's state, its lateness,
 * its calendar hold, what asked for it and what an agent has proposed on it.
 */

const DAY = 86_400_000;

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Whole local days from `now` to `iso`: negative once it has passed. */
export function dayGap(iso: string, now: Date): number {
  return Math.round((startOfDay(new Date(iso)).getTime() - startOfDay(now).getTime()) / DAY);
}

/** "3 Oct", the way the board writes a due date. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** A day gap as a person says it: today, tomorrow, 3 days ago. */
export function dayWords(gap: number): string {
  if (gap === 0) return 'today';
  if (gap === 1) return 'tomorrow';
  if (gap === -1) return 'yesterday';
  return gap > 0 ? `in ${gap} days` : `${-gap} days ago`;
}

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** Parties without repeats, in the order given. */
export function distinct(parties: ReadonlyArray<Party | null | undefined>): Party[] {
  const seen = new Set<string>();
  const out: Party[] = [];
  for (const p of parties) {
    if (!p) continue;
    const key = `${p.kind}:${p.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

const STATE_PHRASE: Record<WireTask['state'], string> = {
  open: 'not started',
  in_progress: 'under way',
  waiting: 'waiting on someone or something',
  done: 'done',
  cancelled: 'cancelled',
};

const FROM_PAGE = /^Came from the page (\/[A-Za-z0-9/_.-]{0,200})/;

/**
 * The page a task was made from by "Add to my tasks": a link only when the
 * address is one of the platform's own pages. An address that begins with a
 * second slash, climbs with dots or names nothing the platform has is shown
 * as no link at all, so a body written by hand cannot send anyone elsewhere.
 */
export function pageSource(body: string | null | undefined): { label: string; to?: string } | null {
  const path = body ? FROM_PAGE.exec(body)?.[1] : undefined;
  if (!path) return null;
  const help = !path.startsWith('//') && !path.includes('..') ? helpFor(path) : null;
  return help ? { label: `The page “${help.title}”`, to: path } : { label: 'A page of the platform' };
}

export interface TaskExplainOptions {
  /** The reader's own id, so their tasks read "You". */
  meId?: string | null | undefined;
  /** On the task's own page: no link back to itself. */
  onPage?: boolean | undefined;
  /** Closing it, where the screen can: the one press a person's own task needs. */
  markDone?: (() => void) | undefined;
  busy?: boolean | undefined;
}

/** The agent that holds a task: named when its department has one, else "an agent of" it. */
function agentOf(task: WireTask, who: Who): Party | null {
  if (!task.assigneeAgent) return null;
  // Named by its own handle when the task carries it (0162).
  const named = task.assigneeAgentHandle ? who.actor(task.assigneeAgentHandle) : null;
  if (named) return named;
  const known = who.agentsOf(task.department);
  if (known.length === 1) return known[0]!;
  return { name: task.department ? `An agent of ${who.label(task.department)}` : 'An agent', kind: 'agent' };
}

function excerpt(text: string, max = 220): string {
  const one = text.replace(/\s+/g, ' ').trim();
  return one.length > max ? `${one.slice(0, max).trimEnd()}…` : one;
}

/**
 * One line a board card carries under its title, so a card says what it
 * wants before it is opened: what it needs from the reader, else the best
 * next step, first sentence only. Made of the same context the panel shows,
 * so the two never disagree.
 */
export function gistOf(c: ItemContext, max = 96): string {
  const text = (c.need ?? c.next.tip).replace(/\s+/g, ' ').trim();
  const first = /^(.+?[.!?])(\s|$)/.exec(text)?.[1] ?? text;
  return first.length > max ? `${first.slice(0, max - 1).trimEnd()}…` : first;
}

export function explainTask(task: WireTask, who: Who, now: Date = new Date(), opts: TaskExplainOptions = {}): ItemContext {
  const dept = task.department ? who.label(task.department) : null;
  const closed = task.state === 'done' || task.state === 'cancelled';
  const request = task.request ?? null;
  const twin = task.procedure === 'twin_task';
  const procedure = task.procedure && !twin ? humanise(task.procedure).toLowerCase() : null;
  const agent = twin ? null : agentOf(task, who);
  const mine = Boolean(opts.meId) && task.assigneeHuman === opts.meId;
  const gap = task.dueAt ? dayGap(task.dueAt, now) : null;
  const late = !closed && task.dueAt !== null && new Date(task.dueAt).getTime() < now.getTime() && gap !== null && gap < 0;
  const lateDays = late && gap !== null ? -gap : 0;
  const page = request ? null : pageSource(task.body);
  const held = !closed ? task.held ?? null : null;
  const heldAgent = held ? who.actor(held.agent) : null;
  const holdAt = task.hold ? new Date(task.hold.startsAt) : null;
  const holdWords = task.hold
    ? new Date(task.hold.startsAt).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  // What it is.
  const stateWords = STATE_PHRASE[task.state];
  const due = task.dueAt ? `, due ${shortDate(task.dueAt)}` : '';
  let summary: string;
  if (request) {
    summary = `${who.label(request.fromDepartment)} asked ${who.label(request.toDepartment)}: “${request.title}”. It reached this list as a task, ${stateWords}${due}.`;
  } else if (twin) {
    summary = `Your twin was asked to do this in the side chat and runs only the plan you confirmed. It is ${stateWords}${due}.`;
  } else if (agent || procedure) {
    summary = `${agent ? agent.name : 'An agent'} has this task${procedure ? `, as the “${procedure}” routine` : ''}. It is ${stateWords}${due}.`;
  } else {
    const own = task.body && !page ? ` It says: “${excerpt(task.body)}”` : page ? ' It was added from a page.' : '';
    summary = `A task${dept ? ` in ${dept}` : ''}, ${stateWords}${due}.${own}`;
  }

  // What it needs from this reader.
  let need: string | undefined;
  if (closed) need = undefined;
  else if (held && task.mayEdit) need = `A decision from you: approve or decline what ${heldAgent?.name ?? 'the agent'} proposed (${plural(held.calls.length, 'action')}).`;
  else if (held) need = undefined;
  else if (request) need = `Answer it from the request: do what ${who.label(request.fromDepartment)} asked and seal the result for them, or refuse with a reason.`;
  else if (twin) need = task.state === 'waiting' ? 'A superadmin to grant the tool it waits on.' : 'Nothing from you unless it is handed back.';
  else if (agent || procedure) need = task.state === 'waiting' ? 'Something it is waiting on; the notes on its page say what.' : 'Nothing from you unless it asks.';
  else if (task.callStep && !task.callStep.outcome) need = 'To be called, and for you to say what came of the call on this page: speaking to them stops the follow-up.';
  else if ((task.waitingOn?.length ?? 0) > 0) need = `${plural(task.waitingOn!.length, 'task')} to be done first: ${task.waitingOn!.map((b) => `“${b.title}”`).join(', ')}.`;
  else if (task.state === 'waiting') need = 'Something from someone else before it can go on.';
  else need = late ? `To be done: it is ${plural(lateDays, 'day')} past its due date.` : task.state === 'in_progress' ? 'To be finished.' : 'To be started.';

  // Why it is in front of them.
  let why: string;
  if (request) why = `${who.label(request.fromDepartment)} asked ${who.label(request.toDepartment)}, and routing ${request.routedTo === 'lead' ? 'found no agent here that holds what it needs, so it came to the lead' : 'gave it to an agent that holds what it needs'}.`;
  else if (twin) why = 'You confirmed this plan in the side chat.';
  else if (agent || procedure) why = `It is part of ${dept ?? 'your department'}’s work and an agent holds it.`;
  else if (mine) why = `It is yours${gap !== null ? `, due ${dayWords(gap)}` : ''}${task.priority <= 2 ? ', and marked as one to do first' : ''}.`;
  else why = task.mayEdit ? 'You may change it because you made it or you lead here.' : `It is part of ${dept ?? 'your department'}’s work.`;

  // Where it came from, and who brought it.
  let source: ItemContext['source'];
  if (request) {
    source = { label: `A request from ${who.label(request.fromDepartment)}`, to: `/requests/${request.id}`, when: shortDate(request.createdAt) };
  } else if (page) {
    source = { ...page, when: shortDate(task.createdAt) };
  } else if (twin) {
    source = { label: 'Your request in the side chat, as you confirmed it', when: shortDate(task.createdAt) };
  } else {
    switch (task.originKind) {
      case 'message':
        source = { label: 'A message in a conversation', when: shortDate(task.createdAt), ...(task.threadId ? { to: `/mail/${task.threadId}` } : {}) };
        break;
      case 'calendar_event':
        source = { label: 'A meeting', when: shortDate(task.createdAt) };
        break;
      case 'chat_session':
        source = { label: 'A conversation in the side chat', when: shortDate(task.createdAt) };
        break;
      case 'trigger':
        source = { label: 'Something the platform noticed and put to the lead', when: shortDate(task.createdAt) };
        break;
      case 'human':
        source = { label: 'Written down by a person', when: shortDate(task.createdAt) };
        break;
      default:
        source = null;
    }
  }

  let broughtBy: ItemContext['broughtBy'];
  if (request) {
    const asker = who.actor(request.requestedBy);
    broughtBy = asker ? { ...asker, role: `for ${who.label(request.fromDepartment)}`, when: shortDate(request.createdAt) } : null;
  } else if (task.createdByKind === 'system') {
    broughtBy = { name: 'The platform', kind: 'system', when: shortDate(task.createdAt) };
  } else {
    const maker = who.actor(task.createdBy);
    broughtBy = maker
      ? { ...maker, ...(task.onBehalfOf ? { role: task.onBehalfOf === opts.meId ? 'for you' : 'for someone else' } : {}), when: shortDate(task.createdAt) }
      : null;
  }

  // Who owns it, and who is on it.
  let owner: ItemContext['owner'];
  if (twin && !agent) owner = task.state === 'open' && task.assigneeHuman ? who.me : { name: 'Your twin', kind: 'twin' };
  else if (agent) owner = agent;
  // A colleague by name where the task carries it (0162); an older reply still reads "A colleague".
  else if (task.assigneeHuman) owner = mine ? who.me : { name: task.assigneeName || 'A colleague', kind: 'person', ...(dept ? { role: `in ${dept}` } : {}) };
  else owner = null;
  const involved = distinct([agent, heldAgent, twin ? { name: 'Your twin', kind: 'twin' } : null]);
  const knows = distinct([
    ...(request ? [who.actor(request.requestedBy), ...who.knowers(request.fromDepartment)] : []),
    ...who.knowers(task.department),
  ])
    .filter((p) => p.name !== 'You')
    .slice(0, 4);

  const facts: NonNullable<ItemContext['facts']> = [
    ['Due', task.dueAt ? `${shortDate(task.dueAt)}${late ? ` (${plural(lateDays, 'day')} late)` : gap !== null && !closed ? ` (${dayWords(gap)})` : ''}` : closed ? null : 'No due date'],
    ['Priority', task.priority <= 2 ? 'First' : null],
    ['Estimate', task.estimateMinutes ? `about ${task.estimateMinutes} minutes` : null],
    ['In your calendar', holdWords],
    ...(request
      ? ([['Needs', request.needs.tools.length + request.needs.scopes.length === 0 ? 'Nothing beyond what your department already holds' : `${plural(request.needs.tools.length, 'tool')} and ${plural(request.needs.scopes.length, 'permission')} your department already holds`]] as NonNullable<ItemContext['facts']>)
      : []),
    ['Closed', task.completedAt ? shortDate(task.completedAt) : null],
    // Only when it changed after it was made; the day it was made is already said.
    ['Last changed', task.updatedAt && !closed && shortDate(task.updatedAt) !== shortDate(task.createdAt) ? shortDate(task.updatedAt) : null],
  ];

  // What to do.
  const taskPage = `/tasks/${task.id}`;
  const pageAction = (label: string, primary = false): StepAction | null => (opts.onPage ? null : { label, to: taskPage, primary });
  const actions: StepAction[] = [];
  let tip: string;
  let instead: string | undefined;
  if (closed) {
    tip = `Nothing is needed from you. It was ${task.state === 'done' ? 'finished' : 'cancelled'}${task.completedAt ? ` on ${shortDate(task.completedAt)}` : ''}.`;
    instead = 'A closed task is not reopened. If it comes back, make a new task and say what changed.';
  } else if (held) {
    if (task.mayEdit) {
      tip = `${heldAgent?.name ?? 'The agent'} proposed ${plural(held.calls.length, 'action')} and your department acts only on approval. Read them, then approve (they run in order and stop at the first refusal) or decline (nothing is done).`;
      instead = 'Until you decide, nothing runs and the proposal stays sealed on this task.';
      const a = pageAction('Read and decide', true);
      if (a) actions.push(a);
    } else {
      tip = `${heldAgent?.name ?? 'The agent'} proposed something that waits on the department’s lead. Nothing is needed from you.`;
      instead = 'If it matters to you, tell the lead in the notes on the task.';
    }
  } else if (request) {
    const from = who.label(request.fromDepartment);
    if (request.state === 'open' || request.state === 'accepted') {
      if (request.routedTo === 'agent' && agent) {
        tip = `${agent.name} is working on what ${from} asked for. Nothing is needed from you unless it hands the request back.`;
        instead = `Open the request to see where it stands, or ask ${from}’s lead if it is urgent.`;
      } else {
        tip = `${from} is waiting for an answer${late ? ` and it is ${plural(lateDays, 'day')} late` : gap !== null ? `, due ${dayWords(gap)}` : ''}. Open the request and either do what it asks and seal the result, or refuse with a reason.`;
        instead = 'It is answered from the request, not closed here. If you need something from them first, say so in the notes on this task.';
        actions.push({ label: 'Answer the request', to: `/requests/${request.id}`, primary: true });
      }
    } else {
      tip = `The request is ${request.state === 'done' ? 'answered' : request.state === 'refused' ? 'refused' : 'closed'}. Nothing is needed from you.`;
      instead = 'Open the request to read how it ended.';
    }
  } else if (twin) {
    if (task.state === 'waiting') {
      tip = who.superadmin
        ? 'Your twin’s task waits on a tool only you can grant. Open it to see which, and grant it to this task, or leave it waiting.'
        : 'Your twin’s task waits on a tool only a superadmin can grant. Ask one to grant it to this task; until then it does not run past that tool.';
      instead = 'Open it to see exactly which tools it waits on and what each needs.';
    } else if (task.state === 'open' && task.assigneeHuman) {
      tip = 'Your twin handed this task back to you with its reason on the task. Read why, then do it yourself.';
      instead = 'Or ask for it again in the side chat, with the part that blocked it said differently.';
    } else if (task.state === 'in_progress') {
      tip = 'Your twin is running it. Nothing is needed from you; the answer and its sources appear on the task’s page.';
    } else {
      tip = 'Your twin runs it on the platform’s next pass. Nothing is needed from you.';
      instead = 'Open it to read the plan you confirmed, step by step.';
    }
    const a = pageAction('Open the task');
    if (a) actions.push(a);
  } else if (agent || procedure) {
    const who2 = agent ? agent.name : 'The agent';
    if (task.state === 'waiting') {
      tip = `${who2} is waiting on something. Read the notes on the task to see what, and answer there if it is you it waits on.`;
    } else if (late) {
      tip = `${who2} has it and it is ${plural(lateDays, 'day')} late. Ask in the notes where it stands, or ask the department’s lead.`;
      instead = 'An agent’s task is not moved on the board; it moves when the agent hands it back or finishes it.';
    } else {
      tip = `Nothing is needed from you. ${who2} has it and will say if that changes.`;
      instead = 'If you want more detail, ask it to elaborate in the discussion on the task’s page.';
    }
  } else if (!mine && task.assigneeHuman) {
    tip = late
      ? `This is ${task.assigneeName ? `${task.assigneeName}’s` : 'a colleague’s'} task and it is ${plural(lateDays, 'day')} late. Ask ${task.assigneeName ?? 'them'} where it stands before you change anything.`
      : `This is ${task.assigneeName ? `${task.assigneeName}’s` : 'a colleague’s'} task. Nothing is needed from you unless it slips.`;
    instead = task.mayEdit ? 'As you lead here you may change it, or say something in its notes.' : 'Say something in its notes if you know more.';
  } else if (holdAt && task.state !== 'waiting') {
    tip = `Time is held in your calendar on ${holdWords}. Do it then, and mark it done when it is finished.`;
    instead = late ? 'It is already late; if the held time is too far off, move the hold from your calendar on Today.' : 'If the time no longer works, move or let go of the hold from your calendar on Today.';
    if (opts.markDone && mine) actions.push({ label: 'Mark done', run: opts.markDone, busy: opts.busy });
  } else if (task.state === 'waiting') {
    tip = 'It is waiting. Chase whoever it waits on, and say who in the notes so the next person knows. Move it back to To do when it is unblocked.';
    instead = 'If nothing will unblock it, say so in the notes and close it.';
    const a = pageAction('Open the task');
    if (a) actions.push(a);
  } else if (task.state === 'in_progress') {
    tip = late
      ? `Under way and ${plural(lateDays, 'day')} late. Finish it, or move the due date from the task’s page if it no longer matters.`
      : 'Under way. Mark it done when it is finished; if you are stuck, move it to Waiting and say what you wait on.';
    instead = 'Put an hour in your calendar from the task’s page if it keeps slipping.';
    if (opts.markDone && mine && task.mayEdit) actions.push({ label: 'Mark done', run: opts.markDone, busy: opts.busy });
  } else if (late) {
    tip = `${plural(lateDays, 'day')} late and not started. Put an hour in your calendar from the task’s page, or move the due date if it no longer matters.`;
    instead = 'If you need something first, move it to Waiting and say what you need.';
    const a = pageAction('Plan it', true);
    if (a) actions.push(a);
  } else if (gap !== null && gap <= 1) {
    tip = `Due ${dayWords(gap)} and not started. Put an hour in your calendar from the task’s page, or move it to Waiting if you need something first.`;
    instead = 'Or start it now and move it to Doing.';
    const a = pageAction('Put time in my calendar', true);
    if (a) actions.push(a);
  } else if (gap !== null) {
    tip = `Due ${dayWords(gap)}. Nothing is urgent yet; put time in your calendar from the task’s page so it does not collide with the day it is due.`;
    instead = 'Or leave it: it moves to late if the date passes.';
  } else {
    tip = 'No due date and not started. Decide now: do it today, put an hour in your calendar, or let it go and close it.';
    instead = 'Add a due date on the task’s page so it shows up on your day.';
    const a = pageAction('Open the task');
    if (a) actions.push(a);
  }

  return {
    subject: { kind: 'task', title: task.title },
    summary,
    ...(need ? { need } : {}),
    why,
    source,
    broughtBy,
    owner,
    ...(involved.length > 0 ? { involved } : {}),
    ...(knows.length > 0 ? { knows } : {}),
    facts,
    next: { tip, ...(instead ? { instead } : {}), ...(actions.length > 0 ? { actions } : {}) },
    ...(opts.onPage ? {} : { open: { to: taskPage, label: 'Open the task' } }),
  };
}

// ---------------------------------------------------------------------------
// A twin's task (Amendment 0011, ADR-0139)
// ---------------------------------------------------------------------------

export type ToolWords = (ids: readonly string[]) => string;

const TWIN_STATE: Record<string, string> = {
  open: 'queued',
  in_progress: 'running',
  waiting: 'waiting on a tool',
  done: 'done',
  cancelled: 'cancelled',
};

interface TwinTaskLike {
  id: string;
  title: string;
  state: string;
  department: string;
  createdAt: string;
  withYou?: boolean | undefined;
}

function twinNext(
  t: TwinTaskLike,
  waitingOn: readonly string[],
  who: Who,
  tools: ToolWords,
  onPage: boolean,
  detail: { refused: number; setUpFirst: number } = { refused: 0, setUpFirst: 0 },
): ItemContext['next'] {
  const link: StepAction[] = onPage ? [] : [{ label: 'Open the task', to: `/tasks/${t.id}` }];
  const names = waitingOn.length > 0 ? tools(waitingOn) : 'a tool';
  if (t.withYou) {
    return {
      tip: 'Your twin handed this back to you, with its reason on the task. Read why, then do it yourself.',
      instead: 'Or ask for it again in the side chat, saying the blocked part differently. Your twin prepares; it never sends.',
      actions: link,
    };
  }
  switch (t.state) {
    case 'waiting': {
      if (detail.setUpFirst > 0 && waitingOn.length === 0) {
        return { tip: 'A tool it needs has no signed manifest yet, so a superadmin has to set it up before it can run. Nothing is needed from you except asking one to.', instead: 'Until then it keeps its plan and does not run past that tool.', actions: link };
      }
      return who.superadmin
        ? {
            tip: `It waits on ${names}, which only you can grant. Grant it to this task from the Workspace, or leave it waiting.`,
            instead: 'Granting is bound to this one task and this one tool, and ends with the task.',
            actions: [...link, { label: 'Open the Workspace', to: '/workspace', primary: true }],
          }
        : {
            tip: `It waits on ${names}, which only a superadmin can grant. Ask one to grant it to this task; until then it does not run past that tool.`,
            instead: 'It keeps the plan you confirmed, so nothing is lost by waiting.',
            actions: link,
          };
    }
    case 'in_progress':
      return { tip: 'Your twin is running it. Nothing is needed from you; the answer and its sources appear on the task’s page.', instead: 'Open it to see each step as it is taken.', actions: link };
    case 'open':
      return { tip: 'Your twin runs it on the platform’s next pass. Nothing is needed from you.', instead: 'Open it to read the plan you confirmed, step by step.', actions: link };
    case 'done':
      return {
        tip: 'Nothing is needed from you. Read the answer, and trust a source only where its page was read back: an excerpt is only what a search said.',
        instead: detail.refused > 0 ? 'A tool was refused on the way; the answer may be thinner than the plan promised.' : 'If it is not what you needed, ask again in the side chat with what was missing.',
        actions: link,
      };
    default:
      return { tip: 'Nothing is needed from you; this task was cancelled.', instead: 'Ask again in the side chat if you still want it done.', actions: link };
  }
}

/** A twin's task as a row of the list: what it was asked, where it stands and what the person must do. */
export function explainTwinTaskRow(t: WireTwinTaskRow, who: Who, tools: ToolWords, onPage = false): ItemContext {
  const dept = who.label(t.department);
  const state = t.withYou ? 'handed back to you' : (TWIN_STATE[t.state] ?? humanise(t.state).toLowerCase());
  return {
    subject: { kind: 'task', title: t.title },
    summary: `You asked your twin to do this in the side chat, in ${dept}. It runs only the plan you confirmed, and is ${state}.`,
    need: t.withYou
      ? 'It is yours to do now.'
      : t.state === 'waiting'
        ? `A superadmin to grant ${t.waitingOn.length > 0 ? tools(t.waitingOn) : 'the tool it waits on'}.`
        : t.state === 'done' || t.state === 'cancelled'
          ? undefined
          : 'Nothing from you.',
    why: 'You read the plan and confirmed it, so it is yours alone: no one else sees it.',
    source: { label: 'Your request in the side chat, as you confirmed it', when: shortDate(t.createdAt) },
    broughtBy: { ...who.me, when: shortDate(t.createdAt) },
    owner: t.withYou ? who.me : { name: 'Your twin', kind: 'twin' },
    ...(who.knowers(t.department).length > 0 ? { knows: who.knowers(t.department).slice(0, 3) } : {}),
    facts: [['Waiting on', t.waitingOn.length > 0 ? tools(t.waitingOn) : null]],
    next: twinNext(t, t.waitingOn, who, tools, onPage),
    ...(onPage ? {} : { open: { to: `/tasks/${t.id}`, label: 'Open the task' } }),
  };
}

const CAP_WORDS: Record<WireTaskCapability['state'], string> = {
  held: 'held',
  granted: 'granted',
  requested: 'waiting on a superadmin',
  unavailable: 'needs setting up first',
  refused: 'refused',
};

/** The twin's task page, in full: the plan, the tools and what each is waiting on. */
export function explainTwinTask(t: WireTwinTask, who: Who, tools: ToolWords, onPage = true): ItemContext {
  const waitingOn = t.capabilities.filter((c) => c.state === 'requested').map((c) => c.toolId);
  const setUp = t.capabilities.filter((c) => c.state === 'unavailable');
  const refused = t.capabilities.filter((c) => c.state === 'refused');
  const base = explainTwinTaskRow({ id: t.id, title: t.title, state: t.state, department: t.department, createdAt: t.createdAt, waitingOn, ...(t.withYou !== undefined ? { withYou: t.withYou } : {}) }, who, tools, onPage);
  const planned = t.plan?.output ?? null;
  const count = (state: WireTaskCapability['state']) => t.capabilities.filter((c) => c.state === state).length;
  return {
    ...base,
    summary: `${t.plan?.subject ? `${t.plan.subject}. ` : ''}${base.summary}`,
    facts: [
      ['You get back', planned],
      ['Tool calls', t.plan ? `at most ${t.plan.maxCalls}` : null],
      ['Tools held or granted', count('held') + count('granted') > 0 ? tools(t.capabilities.filter((c) => c.state === 'held' || c.state === 'granted').map((c) => c.toolId)) : null],
      ['Waiting on a superadmin', waitingOn.length > 0 ? tools(waitingOn) : null],
      ['Needs setting up first', setUp.length > 0 ? tools(setUp.map((c) => c.toolId)) : null],
      ['Refused', refused.length > 0 ? tools(refused.map((c) => c.toolId)) : null],
      ['Done', t.completedAt ? shortDate(t.completedAt) : null],
    ],
    next: twinNext({ id: t.id, title: t.title, state: t.state, department: t.department, createdAt: t.createdAt, withYou: t.withYou }, waitingOn, who, tools, onPage, { refused: refused.length, setUpFirst: setUp.length }),
    // Said once so a reader sees what each state of a tool means without opening it.
    why: `${base.why ?? ''}${t.capabilities.length > 0 ? ` Its tools: ${t.capabilities.map((c) => `${tools([c.toolId])} ${CAP_WORDS[c.state]}`).join('; ')}.` : ''}`.trim(),
  };
}
